// =============================================================================
// src/app/dom.ts — Helpers tipados de acesso ao DOM (boundary unico, greppavel).
//
// Proveniencia: substitui o `document.getElementById`/`querySelectorAll` espalhado
//   por assets/unified-app.js (ex.: linhas 354, 446, 626-631, 716). Centralizar o
//   acesso ao DOM aqui garante (1) erro com o id/seletor ofensivo no texto
//   (CLAUDE.md regra 11), (2) tipos explicitos no retorno (sem `any`), e (3) um
//   ponto unico de grep para qualquer toque no DOM (`byId`/`qs`/`qsa`/`on`).
//
// SRP: so resolucao/escuta de DOM. Sem dados, sem timers, sem regra de negocio.
// =============================================================================

/**
 * Resolve um elemento por id ou lanca com o id ofensivo no texto.
 * Use quando o elemento DEVE existir (ponto de montagem do shell, overlay fixo);
 * para acesso opcional, prefira `qs` que devolve null.
 *
 * @example
 *   const popup = byId('u-popup'); // HTMLElement garantido ou throw
 */
export function byId(id: string): HTMLElement {
  const element = document.getElementById(id);
  if (!element) {
    throw new Error(`dom.ts: elemento ausente id=${JSON.stringify(id)}`);
  }
  return element;
}

/**
 * Primeiro elemento que casa o seletor CSS, tipado por `T`, ou null se ausente.
 * O escopo de busca default e `document`; passe `root` para limitar a uma subarvore.
 *
 * @example
 *   const title = qs<HTMLDivElement>('.u-section-title', section);
 */
export function qs<T extends Element = HTMLElement>(
  selector: string,
  root: ParentNode = document,
): T | null {
  return root.querySelector<T>(selector);
}

/**
 * Todos os elementos que casam o seletor, tipados por `T`, como array materializado.
 * Devolve `T[]` (nao NodeList) para iteracao com map/filter/forEach sem conversao,
 * espelhando o helper `UI.qsa` legado (assets/unified-app.js 822-824).
 *
 * @example
 *   qsa<HTMLButtonElement>('.u-comp-mode-tab').forEach((tab) => ...);
 */
export function qsa<T extends Element = HTMLElement>(
  selector: string,
  root: ParentNode = document,
): T[] {
  return Array.from(root.querySelectorAll<T>(selector));
}

/**
 * Anexa um listener tipado e devolve um disposer que o remove. Centraliza o
 * `addEventListener` para que os modulos liguem/desliguem listeners sem repetir
 * o par add/remove (DRY) e sem perder o tipo do evento por nome.
 *
 * @example
 *   const off = on(byId('u-pop-cta'), 'click', () => sendPrompt('...'));
 *   off(); // remove o listener
 */
export function on<K extends keyof HTMLElementEventMap>(
  element: HTMLElement,
  eventName: K,
  handler: (event: HTMLElementEventMap[K]) => void,
): () => void {
  element.addEventListener(eventName, handler);
  return () => element.removeEventListener(eventName, handler);
}

/**
 * Escapa texto para innerHTML (& < > " ') — home canônico, greppável, do escape
 * que vinha duplicado em SearchPalette._esc/workspace/crisis. Use em TODO texto de
 * origem dinâmica (query, item, saída de LLM) antes de concatenar em HTML.
 *
 * @example
 *   `<strong>${escapeHtml(insight.headline)}</strong>`
 */
export function escapeHtml(raw: string): string {
  return String(raw)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
